// views/Profile/components/TransactionsTable.jsx
import React from 'react';
import {
  Receipt,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import EmptyState from '../../../components/ui/EmptyState';
import './TransactionsTable.css';


const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat('en-US').format(value)}`;
};

const dayKey = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const formatDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return 'Today';
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return 'Yesterday';
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: today.getFullYear() === d.getFullYear() ? undefined : 'numeric',
  });
};

const formatTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// Trim a UUID-ish id to a short prefix that's still recognizable in the
// row but doesn't hog horizontal space. The full id lives in the row
// drawer for copy/paste.
const shortId = (id) => {
  if (!id) return null;
  return String(id).split('-')[0]?.slice(0, 8) ?? null;
};

// First piece of context to show next to the resource name. Prefers an
// explicit description (the backend sometimes includes one), then the
// short execution / call ids that link back to the row drawer.
const resourceContext = (row) => {
  if (row.description) return row.description;
  if (row.executionId) return `exec ${shortId(row.executionId)}`;
  if (row.assistantCallId) return `call ${shortId(row.assistantCallId)}`;
  if (row.tableOperationId) return `op ${shortId(row.tableOperationId)}`;
  return null;
};

// Sentence-case throughout so the column reads consistently — no
// mixing of "Workflow run" with "Workflow Step" because the fallback
// title-cased every word.
const RESOURCE_LABELS = {
  WORKFLOW_EXECUTION: 'Workflow run',
  WORKFLOW_STEP: 'Workflow step',
  ASSISTANT_REQUEST: 'Agent request',
  ASSISTANT_CALL: 'Agent call',
  DATABASE_CALL: 'Database call',
  DATABASE_READ: 'Database read',
  DATABASE_WRITE: 'Database write',
  DATABASE_QUERY: 'Database query',
  DATA_TRANSFER_GB: 'Data transfer',
  TABLE_OPERATION: 'Table operation',
  INTEGRATION_CALL: 'Integration call',
  BONUS: 'Bonus credit',
};

const humanizeResource = (raw) => {
  if (!raw) return '—';
  if (RESOURCE_LABELS[raw]) return RESOURCE_LABELS[raw];
  const words = raw.split('_');
  return words
    .map((word, i) =>
      i === 0
        ? word.charAt(0) + word.slice(1).toLowerCase()
        : word.toLowerCase()
    )
    .join(' ');
};

const TYPE_META = {
  DEDUCTION: { label: 'Use', tone: 'negative' },
  RECHARGE: { label: 'Recharge', tone: 'positive' },
  REFUND: { label: 'Refund', tone: 'positive' },
  BONUS: { label: 'Bonus', tone: 'accent' },
};

const POOL_LABEL = {
  period: 'Period',
  extra: 'Extra',
  mixed: 'Mixed',
};

/**
 * TransactionsTable — cursor-paginated ledger.
 *
 * Hooks driven: parent supplies one page of items + loading + error +
 * the two page moves. The component is dumb on data
 * fetching but knows enough about the schema (signed amount, pool,
 * type, resourceType) to color-code and humanize.
 */
export default function TransactionsTable({
  items = [],
  loading = false,
  hasMore = false,
  page = 0,
  hideZeroAmount = false,
  onToggleZeroAmount,
  hasPrevPage = false,
  hasNextPage = false,
  onPrevPage,
  onNextPage,
  error = null,
  onRowClick,
  onResetCursor,
  title = 'Transactions',
  filters = null,
}) {
  // Audit-at-zero rows (e.g. WORKFLOW_STEP entries that record a step
  // ran but charged 0 coins) flood the ledger and bury the actual
  // charges. Hidden by default; the badge in the header reveals them.
  // ----- Paginación, del servidor ------------------------------------
  //
  // `items` es **una** página, no un libro mayor que crece. Antes el hook
  // acumulaba páginas por cursor y esto las rebanaba de a doce: los números
  // que salían abajo solo llegaban hasta donde alguien ya había bajado, con
  // unos puntos suspensivos prometiendo páginas que no se podían pulsar.
  // Era scroll infinito disfrazado de paginador.
  //
  // El backend pagina por cursor y no expone desplazamiento, así que hay
  // siguiente y anterior y no hay salto a la página nueve. Eso no es una
  // limitación que esconder: es lo que el paginador puede prometer.
  // Sin filtrar acá. Los ceros los descarta la consulta, así que `items` ya
  // es lo que hay que mostrar y una página llena viene llena. Filtrando
  // después, una página que llegaba entera de ceros quedaba en blanco y el
  // paginador seguía ofreciendo la siguiente.
  const visibleItems = items;
  const pageItems = items;

  const canPrev = hasPrevPage;
  const canNext = hasNextPage;
  const goNext = () => canNext && onNextPage?.();
  const goPrev = () => canPrev && onPrevPage?.();

  // Hide the POOL column when every visible row has the same pool —
  // there's no information left to convey, just visual noise. The
  // value still surfaces inline next to the resource name.
  const uniquePools = new Set(
    visibleItems.map((row) => row.pool ?? null)
  );
  const showPoolColumn = uniquePools.size > 1;

  // Track the previous day so we can stamp a single divider per day
  // group instead of repeating the date on every row.
  let lastDay = null;

  const showSkeleton = loading && items.length === 0;
  const showEmpty = !loading && !error && items.length === 0;
  const showFilteredEmpty =
    !loading && !error && items.length === 0 && hideZeroAmount;
  const showCursorError = error && error.code === 'BAD_REQUEST';
  const showOtherError = error && !showCursorError;
  const colCount = showPoolColumn ? 5 : 4;

  return (
    <section className="txn-table">
      <header className="txn-table__header">
        <div className="txn-table__title">
          <Receipt size={16} aria-hidden="true" />
          <h2>{title}</h2>
        </div>
        <div className="txn-table__header-actions">
          {/* Un solo botón que alterna. Antes eran dos, y el que ofrecía
              mostrarlos llevaba el conteo de los escondidos — un número que
              ya no existe, porque los ceros no llegan. */}
          {onToggleZeroAmount && (
            <button
              type="button"
              className={`txn-table__zero-toggle${
                hideZeroAmount ? ' txn-table__zero-toggle--active' : ''
              }`}
              onClick={() => onToggleZeroAmount(!hideZeroAmount)}
              aria-pressed={hideZeroAmount}
            >
              {hideZeroAmount
                ? 'Showing charged events only'
                : 'Hide zero-amount events'}
            </button>
          )}
        </div>
      </header>

      {filters && <div className="txn-table__filters">{filters}</div>}

      <div className="txn-table__scroll" role="region" aria-label="Transactions ledger">
        <table className="txn-table__grid">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Type</th>
              <th scope="col">Resource</th>
              <th scope="col" className="txn-table__th-right">Amount</th>
              {showPoolColumn && <th scope="col">Pool</th>}
            </tr>
          </thead>
          <tbody>
            {showSkeleton &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`} className="txn-table__row txn-table__row--skeleton">
                  <td colSpan={colCount}>
                    <div className="txn-table__sk-bar" />
                  </td>
                </tr>
              ))}
            {!showSkeleton &&
              pageItems.map((row) => {
                const typeMeta =
                  TYPE_META[row.transactionType] ?? {
                    label: row.transactionType,
                    tone: 'neutral',
                  };
                const isNegative = row.amount < 0;
                const isZero = row.amount === 0;
                const rowDay = dayKey(row.createdAt);
                const showDayDivider = rowDay && rowDay !== lastDay;
                if (showDayDivider) lastDay = rowDay;
                const context = resourceContext(row);

                return (
                  <React.Fragment key={row.id}>
                    {showDayDivider && (
                      <tr
                        className="txn-table__day-row"
                        aria-hidden="true"
                      >
                        <td colSpan={colCount}>
                          <span className="txn-table__day-label">
                            {formatDay(row.createdAt)}
                          </span>
                        </td>
                      </tr>
                    )}
                    <tr
                      className="txn-table__row txn-table__row--clickable"
                      onClick={() => onRowClick?.(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick?.(row);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open transaction ${row.id}`}
                    >
                      <td className="txn-table__date">
                        {formatTime(row.createdAt)}
                      </td>
                      <td>
                        <span
                          className={`txn-table__type-badge txn-table__type-badge--${typeMeta.tone}`}
                        >
                          {typeMeta.label}
                        </span>
                      </td>
                      <td className="txn-table__resource">
                        <span className="txn-table__resource-name">
                          {humanizeResource(row.resourceType)}
                        </span>
                        {context && (
                          <span className="txn-table__resource-context">
                            {context}
                          </span>
                        )}
                      </td>
                      <td
                        className={`txn-table__amount${
                          isZero
                            ? ' txn-table__amount--zero'
                            : isNegative
                            ? ' txn-table__amount--negative'
                            : ' txn-table__amount--positive'
                        }`}
                      >
                        {formatCoins(row.amount)}
                      </td>
                      {showPoolColumn && (
                        <td>
                          {row.pool ? (
                            <span
                              className={`txn-table__pool txn-table__pool--${row.pool}`}
                            >
                              {POOL_LABEL[row.pool] ?? row.pool}
                            </span>
                          ) : (
                            <span className="txn-table__pool-empty">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {showCursorError && (
        <div className="txn-table__cursor-error">
          <AlertCircle size={16} aria-hidden="true" />
          <span>{error.message || 'Filters reset due to invalid cursor'}</span>
          {onResetCursor && (
            <button
              type="button"
              className="txn-table__cursor-reset"
              onClick={onResetCursor}
            >
              Reset filters
            </button>
          )}
        </div>
      )}

      {showOtherError && (
        <div className="txn-table__error">
          <AlertCircle size={20} aria-hidden="true" />
          <p>{error.message || 'Unable to load transactions'}</p>
          {error.requestId && (
            <small className="txn-table__request-id">
              ref: {error.requestId}
            </small>
          )}
        </div>
      )}

      {showEmpty && (
        <EmptyState
          variant="neutral"
          description="No transactions in this range."
        />
      )}

      {showFilteredEmpty && (
        <EmptyState
          variant="neutral"
          description="Nothing was charged in this range. Turn the filter off to see the events that cost nothing."
        />
      )}

      {!showSkeleton && visibleItems.length > 0 && (canPrev || canNext) && (
        <nav className="txn-table__pagination" aria-label="Transactions pages">
          <button
            type="button"
            className="txn-table__page-btn txn-table__page-btn--nav"
            onClick={goPrev}
            disabled={!canPrev || loading}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>

          {/* El número de la página y nada más. Una lista de números
              clicables prometía saltos que el cursor del backend no puede
              dar; decir en cuál estás sí es cierto. */}
          <span className="txn-table__page-current">Page {page + 1}</span>

          <button
            type="button"
            className="txn-table__page-btn txn-table__page-btn--nav"
            onClick={goNext}
            disabled={!canNext || loading}
            aria-label="Next page"
          >
            {loading ? (
              <span className="txn-table__page-spinner" aria-hidden="true" />
            ) : (
              <ChevronRight size={16} aria-hidden="true" />
            )}
          </button>
        </nav>
      )}
    </section>
  );
}
